import { notifyOnError, throttle } from '~/rage/decorators';
import { IVehiclePaint, VehiclePaintType } from '~/shared';
import { BackendService } from '../BackendService';
import { GameConfigService } from '../GameConfigService';
import { RpcService } from '../RpcService';
import { IVehicleService } from '../VehicleService';
import { AbstractVehiclePaintShopService } from './AbstractVehiclePaintShopService';
import { IVehiclePaintShopService } from './vehiclePaintShopServiceTypes';

export class VehiclePaintShopService
	extends AbstractVehiclePaintShopService
	implements IVehiclePaintShopService
{
	constructor(
		private readonly rpc: RpcService,
		private readonly gameConfig: GameConfigService,
		private readonly backend: BackendService,
		private readonly vehicles: IVehicleService,
	) {
		super();

		gameConfig.getConfigs().subscribe(() => {
			this.updateColors();
		});
	}

	private updateColors() {
		const config = this.gameConfig.configs?.vehiclesPaint;
		if (!config) {
			return;
		}

		// Types
		this.paintTypes.next(config.types);

		// Colors
		const pearlColors = config.paintColors.filter(
			(e) => e.type === VehiclePaintType.Pearl,
		);
		this.pearlColors.next(pearlColors);

		const indexedColors = config.paintColors.filter(
			(e) => e.type !== VehiclePaintType.Pearl,
		);
		this.indexedColors.next(indexedColors);

		this.rimsColors.next(config.rimsColors);

		this.brakesColors.next(config.brakesColors);

		// Rims
		this.rimsColorPrice.next(config.priceRimsColor);

		// Tints
		this.windowTints.next(config.windowsTints);
	}

	@notifyOnError
	public async getOwnedVehiclePaint(): Promise<IVehiclePaint> {
		return await this.backend.invoke<IVehiclePaint>('vehiclesPaint:getOwned', {
			vehicleId: this.vehicles.getLocalOwnedVehicleId().getValue(),
		});
	}

	public async restoreColors(): Promise<void> {
		return await this.rpc.invokeClient('vehiclePaintShop:restoreColors');
	}

	@throttle(250)
	public async previewBodyColor(
		colorHexOrIndex: string | number,
		paintType: VehiclePaintType,
		pearlColorIndex: number | null,
	): Promise<void> {
		return await this.rpc.invokeClient('vehiclePaintShop:previewBodyColor', {
			colorHexOrIndex,
			paintType,
			pearlColorIndex,
		});
	}

	@throttle(250)
	public async previewInteriorColor(colorHex: string): Promise<void> {
		return await this.rpc.invokeClient(
			'vehiclePaintShop:previewInteriorColor',
			{
				colorHex,
			},
		);
	}

	@throttle(250)
	public async previewBrakesColor(colorIndex: number): Promise<void> {
		return await this.rpc.invokeClient('vehiclePaintShop:previewBrakesColor', {
			color: colorIndex,
		});
	}

	@throttle(250)
	public async previewRimsColor(colorIndex: number | null): Promise<void> {
		return await this.rpc.invokeClient('vehiclePaintShop:previewRimsColor', {
			color: colorIndex,
		});
	}

	@throttle(250)
	public async previewWindowTint(tintValue: number | null): Promise<void> {
		return await this.rpc.invokeClient('vehiclePaintShop:previewWindowTint', {
			tint: tintValue,
		});
	}

	@notifyOnError
	public async buy(paint: Partial<IVehiclePaint>): Promise<void> {
		return await this.backend.invoke('vehiclesPaint:buy', {
			paint,
			vehicleId: this.vehicles.getLocalOwnedVehicleId().getValue(),
		});
	}

	public async exit(): Promise<void> {
		return await this.rpc.invokeClient('vehiclePaintShop:exit');
	}
}
